/**
 * OjbMemberPicker — typeahead against /ojb-team for adding OJB staff as
 * attendees. Used by the Attendees section of the editor.
 *
 * Behavior:
 *   - On focus, fetches the list (short query)
 *   - As user types, filters server-side via q= param
 *   - Keyboard: ArrowDown/Up to navigate, Enter to select, Esc to close
 *   - Selected name fires onSelect(name); input clears
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { AttendeeAvatar } from './AttendeeAvatar.tsx';

export interface OjbMember {
  id: string;
  fullName: string;
  email?: string | null;
  office: string | null;
  title: string | null;
  headshotUrl: string | null;
}

interface Props {
  /** Callback when a member is picked — caller adds them to the attendee group.
   *  Receives the full roster member when one was matched (so callers can
   *  capture email, title, office, etc.) or `null` when the user pressed Enter
   *  on a free-typed name with no match. */
  onSelect: (fullName: string, member: OjbMember | null) => void;
  /** Optional: names already in the group, excluded from suggestions */
  excludeNames?: string[];
  placeholder?: string;
}

export function OjbMemberPicker({
  onSelect,
  excludeNames = [],
  placeholder = 'Add OJB staff…',
}: Props) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const suggestions = useQuery({
    queryKey: ['ojb-team', query],
    queryFn: () => api.get<{ members: OjbMember[] }>('/ojb-team', { q: query, limit: 8 }),
    enabled: isOpen,
    staleTime: 60_000,
  });

  const filtered = (suggestions.data?.members ?? []).filter(
    (m) => !excludeNames.includes(m.fullName),
  );

  function pick(m: OjbMember) {
    onSelect(m.fullName, m);
    setQuery('');
    setActiveIndex(0);
    setIsOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || filtered.length === 0) {
      if (e.key === 'Enter') {
        // ALWAYS preventDefault on Enter inside this input — even if
        // there's nothing to add — because we live inside an outer
        // <form> (NewMeetingRoute) and a stray Enter would otherwise
        // submit it and create a half-baked draft. Empty Enter is a
        // no-op; non-empty adds as a free-typed entry.
        e.preventDefault();
        if (query.trim()) {
          onSelect(query.trim(), null);
          setQuery('');
        }
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        {...NO_AUTOFILL}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-2 py-1 text-[12px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
      />
      {isOpen && filtered.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-smoke/30 bg-white text-xs shadow-md">
          {filtered.map((m, i) => (
            <li key={m.id}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(m);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={
                  'flex w-full items-center gap-2 px-2 py-1.5 text-left transition-colors ' +
                  (i === activeIndex ? 'bg-pine/10 text-pine' : 'text-charcoal hover:bg-cloud/50')
                }
              >
                <AttendeeAvatar name={m.fullName} headshotUrl={m.headshotUrl} size={22} />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{m.fullName}</div>
                  {(m.title || m.office) && (
                    <div className="truncate text-[10px] text-smoke">
                      {m.title}
                      {m.title && m.office && ' · '}
                      {m.office}
                    </div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {isOpen && query && filtered.length === 0 && !suggestions.isLoading && (
        <div className="absolute z-20 mt-1 w-full rounded border border-smoke/30 bg-white px-2 py-1.5 text-xs text-smoke shadow-md">
          No matches. Press Enter to add &ldquo;{query}&rdquo; anyway.
        </div>
      )}
    </div>
  );
}
